import React from "react";
//#region components
import { Grid } from "@mui/material";
import { AutoCompleteInput, SimpleTooltip } from "components/common";
//#endregion
//#region types
import { TimeReportingEnums } from "models/enums";
import { TimeReportingFormTypes } from "models/formTypes";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
import { ProjectCategoryPickerProps } from "models/propTypes/timeReportingPropTypes";
import { FormFieldConstants } from "utils/constants";
import { FormikProps, useFormikContext } from "formik";
//#endregion
import { FormHelper } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";
import clsx from "clsx";

const ProjectCategoryPickerField: React.FC<ProjectCategoryPickerProps> = (props) => {
  const { getAvailableCategories } = props;
  const formikContext = useFormikContext<
    TimeReportingFormTypes.WeeklyTimeReport | TimeReportingFormTypes.MonthlyTimeReport
  >();
  const { setFieldValue, values } = formikContext;

  const headerName = React.useMemo(
    () => (props.name?.includes("header") ? values.headerRow?.name : undefined),
    [props.name]
  );

  const fieldInfo = React.useMemo(() => FormHelper.getTableRowFieldInfo(props.name ?? ""), [props.name]);
  const row = React.useMemo(() => values.rows.find((_, index) => index === fieldInfo?.rowIndex), [fieldInfo, values]);
  const isProjectTypeRow = React.useMemo(
    () => row?.type === TimeReportingEnums.TimeReportType.project,
    [fieldInfo, row]
  );

  const getCategories = (value: string, formattedValue?: string, formikProps?: FormikProps<any>) => {
    return getAvailableCategories ? getAvailableCategories(formikContext, row?.projectId) : [];
  };

  const onItemSelected = (item: AutocompleteListItem | null) => {
    if (fieldInfo?.rowIndex !== undefined) {
      const updatedRows = values.rows.map((ur, index) =>
        fieldInfo.rowIndex === index ? { ...ur, categoryId: Number(item?.id), categoryName: item?.value } : ur
      );
      setFieldValue(FormFieldConstants.TimeReporting.WeeklyTimeReport.Rows, updatedRows);
    }
  };

  const isCategorySelectionDisabled = () => {
    return props.isCategorySelectionDisabled && props.isCategorySelectionDisabled(formikContext, fieldInfo?.rowIndex);
  };

  const [isCategoryNameTruncated, setIsCategoryNameTruncated] = React.useState(false);

  // SimpleTooltip's showOnlyOnOverflow measures its direct child once, on mount. What truncates here is
  // the input inside AutoCompleteInput, and its text changes with every selection, so measure on hover and
  // focus. Only while it shows the selected name: mid-search it holds the query, not what the tooltip says.
  const measureCategoryNameTruncation = (event: React.SyntheticEvent<HTMLElement>) => {
    const input = event.currentTarget.querySelector("input");
    setIsCategoryNameTruncated(!!input && input.value === row?.categoryName && input.scrollWidth > input.clientWidth);
  };

  return (
    <Grid
      container
      item
      direction="column"
      className={clsx(cssExports.inputFormControl, cssExports.projectCategoryPicker)}
    >
      <Grid item className={clsx(cssExports.readOnlyField)}>
        <Grid className={clsx(cssExports.fieldContent)}>{headerName ?? row?.name}</Grid>
      </Grid>
      {isProjectTypeRow &&
        (props.isReadOnly || isCategorySelectionDisabled() ? (
          <Grid item className={clsx(cssExports.projectCategoryReadOnlyField)}>
            {row?.categoryName}
          </Grid>
        ) : (
          <SimpleTooltip text={row?.categoryName || " "} hidden={!isCategoryNameTruncated || !row?.categoryName}>
            <Grid item onMouseEnter={measureCategoryNameTruncation} onFocus={measureCategoryNameTruncation}>
              <AutoCompleteInput
                name={props.name ?? "category"}
                value={row?.categoryName}
                items={getCategories}
                onItemSelect={onItemSelected}
                classes={{ popper: cssExports.projectCategoryPickerPopper }}
              />
            </Grid>
          </SimpleTooltip>
        ))}
    </Grid>
  );
};

export default ProjectCategoryPickerField;
